import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Mail, Phone, Download, Loader2, Trash2, Linkedin, FileText } from "lucide-react";
import { AdminTitle } from "./AdminLayout";
import { Confirm } from "./Confirm";
import { http, apiError, formatDateTime, telHref } from "../../lib/api";
import { fmtSize } from "../../components/site/FileUploader";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import { ApplicantEmails } from "./ApplicantEmails";

export const APP_STATUS = {
  received: "Application Received", under_review: "Under Review", shortlisted: "Shortlisted", interview: "Interview",
  offer_extended: "Offer Extended", hired: "Hired", rejected: "Rejected",
};
const statusCls = (s) => (s === "received" ? "border-[var(--gold)] text-gold" : s === "hired" ? "border-emerald-400/50 text-emerald-300" : s === "rejected" ? "border-slate-500/50 text-slate-400" : "border-sky-400/40 text-sky-300");
const Pill = ({ children, cls = "border-white/20 text-slate-300" }) => <span className={`font-mono text-[0.62rem] uppercase px-2 py-1 border ${cls}`}>{children}</span>;
const Field = ({ k, v }) => (v ? <div className="grid grid-cols-[130px_1fr] gap-3 py-2.5"><dt className="text-slate-400">{k}</dt><dd className="text-slate-100 whitespace-pre-line break-words">{v}</dd></div> : null);

const download = async (a, f) => {
  try {
    const { data } = await http.get(`/admin/careers/applications/${a.id}/files/${f.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = url;
    link.download = f.name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error("Could not download the file.");
  }
};

const FileBtn = ({ a, f, label }) => (
  <button type="button" onClick={() => download(a, f)} className="w-full flex items-center gap-3 p-3 border hairline hover:border-[var(--gold)] text-left transition-colors" data-testid={`app-file-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <FileText className="w-4 h-4 text-gold shrink-0" /><span className="flex-1 min-w-0"><span className="block text-xs text-slate-400">{label}</span><span className="block text-sm text-slate-100 truncate">{f.name}</span></span>
    <span className="text-xs text-slate-400">{fmtSize(f.size)}</span><Download className="w-4 h-4 text-slate-300" />
  </button>
);

const Detail = ({ a, onChange, onDeleted }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const base = `/admin/careers/applications/${a.id}`;
  const run = async (key, fn, msg) => {
    setBusy(key);
    try {
      const { data } = await fn();
      if (msg) toast.success(msg);
      onChange(data);
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="space-y-8" data-testid="app-detail">
      <div>
        <p className="font-mono text-xs text-gold">{a.ref}</p>
        <h2 className="font-display text-3xl mt-1">{a.full_name}</h2>
        <p className="text-sm text-slate-300 mt-1">{a.job_title} · {a.department}</p>
        <div className="flex flex-wrap gap-2 mt-3"><Pill cls={statusCls(a.status)}>{APP_STATUS[a.status]}</Pill><Pill>Received {formatDateTime(a.created_at)}</Pill></div>
        <div className="flex flex-wrap gap-2 mt-4">
          <a href={`mailto:${a.email}`} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="app-email-link"><Mail className="w-3.5 h-3.5" /> Email</a>
          <a href={telHref(a.phone)} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="app-call-link"><Phone className="w-3.5 h-3.5" /> Call</a>
          {a.linkedin && <a href={/^https?:\/\//.test(a.linkedin) ? a.linkedin : `https://${a.linkedin}`} target="_blank" rel="noopener noreferrer nofollow" className="btn-ghost !py-2 !px-3 !text-xs" data-testid="app-linkedin-link"><Linkedin className="w-3.5 h-3.5" /> LinkedIn</a>}
        </div>
      </div>
      <div>
        <label className="label" htmlFor="app-status">Applicant status</label>
        <select id="app-status" className="field" value={a.status} disabled={busy === "status"} onChange={(e) => run("status", () => http.patch(base, { status: e.target.value }), "Status updated")} data-testid="app-status-select">
          {Object.entries(APP_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <ApplicantEmails a={a} onChange={onChange} />
      <dl className="divide-y divide-white/10 text-sm border-y hairline">
        <Field k="Email" v={a.email} /><Field k="Phone" v={a.phone} /><Field k="Location" v={[a.city, a.state, a.country].filter(Boolean).join(", ")} />
        <Field k="Preference" v={a.employment_preference} /><Field k="Experience" v={a.years_experience ? `${a.years_experience} years` : ""} />
        <Field k="Message" v={a.message} /><Field k="Consent given" v={formatDateTime(a.consent_at)} />
      </dl>
      <section className="space-y-2">
        <p className="eyebrow">Documents</p>
        {a.resume && <FileBtn a={a} f={a.resume} label="Resume" />}
        {a.cover_letter && <FileBtn a={a} f={a.cover_letter} label="Cover letter" />}
        <p className="text-xs text-slate-400">Files are downloaded, never opened in the browser. Scan them with your antivirus before opening.</p>
      </section>
      <section>
        <p className="eyebrow mb-3">Internal notes</p>
        <form className="flex gap-2" onSubmit={async (e) => { e.preventDefault(); if (note.trim() && await run("note", () => http.post(`${base}/notes`, { text: note }), "Note added")) setNote(""); }}>
          <input className="field !py-2.5 flex-1" placeholder="Add a private note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="app-note-input" />
          <button className="btn-gold !py-2.5 !px-4" disabled={busy === "note" || !note.trim()} data-testid="app-note-submit">{busy === "note" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}</button>
        </form>
        <ul className="mt-4 space-y-3" data-testid="app-notes">
          {[...(a.notes_log || [])].reverse().map((n) => <li key={n.id} className="border-l-2 border-white/15 pl-3 text-sm"><p className="text-slate-100 whitespace-pre-line">{n.text}</p><p className="text-xs text-slate-400 mt-1">{n.author} · {formatDateTime(n.at)}</p></li>)}
        </ul>
      </section>
      {(a.history || []).length > 0 && (
        <section><p className="eyebrow mb-3">Status history</p>
          <ul className="space-y-1.5 text-xs text-slate-300">{[...a.history].reverse().map((h, i) => <li key={i}>{formatDateTime(h.at)} · {h.by}: {APP_STATUS[h.from]} → {APP_STATUS[h.to]}</li>)}</ul>
        </section>
      )}
      <section className="text-xs text-slate-400">Emails: applicant confirmation {a.confirmation_status || "pending"}{(a.notification_status || []).map((n) => ` · ${n.to} ${n.status}`).join("")}</section>
      <Confirm title="Delete this application?" text="The application and its documents will be removed permanently." onConfirm={async () => { try { await http.delete(base); toast.success("Application deleted"); onDeleted(); } catch (e) { toast.error(apiError(e)); } }}>
        <button className="btn-ghost hover:!border-rose-400 !text-rose-300" data-testid="app-delete-button"><Trash2 className="w-4 h-4" /> Delete application</button>
      </Confirm>
    </div>
  );
};

export default function ApplicationsAdmin() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const filters = { job_id: params.get("job") || undefined, status: params.get("status") || undefined, q: params.get("q") || undefined };
  const openId = params.get("id") || "";
  const { data, isLoading } = useQuery({ queryKey: ["applications", filters], queryFn: async () => (await http.get("/admin/careers/applications", { params: filters })).data });
  const { data: jobs } = useQuery({ queryKey: ["admin-jobs"], queryFn: async () => (await http.get("/admin/careers/jobs")).data });
  const { data: detail } = useQuery({ queryKey: ["application", openId], queryFn: async () => (await http.get(`/admin/careers/applications/${openId}`)).data, enabled: !!openId, retry: false });
  const items = Array.isArray(data?.items) ? data.items : [];
  const set = (patch) => {
    const n = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setParams(n, { replace: true });
  };
  const refresh = (doc) => {
    if (doc?.id) qc.setQueryData(["application", doc.id], doc);
    ["applications", "admin-stats", "admin-jobs"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };
  return (
    <div data-testid="admin-applications">
      <AdminTitle title="Job Applications" sub="Private. Only the owner can see applicants, their documents and statuses." />
      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <form className="relative flex-1" onSubmit={(e) => { e.preventDefault(); set({ q: q.trim() }); }}>
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="field !py-2.5 !pl-10" placeholder="Search name, email, position or APP ref… (Enter)" value={q} onChange={(e) => setQ(e.target.value)} onBlur={() => set({ q: q.trim() })} data-testid="app-search-input" />
        </form>
        <select className="field lg:!w-72 !py-2.5" value={params.get("job") || ""} onChange={(e) => set({ job: e.target.value })} data-testid="app-filter-job">
          <option value="">All positions</option>{(jobs?.items || []).map((j) => <option key={j.id} value={j.id}>{j.title} ({j.applications})</option>)}
        </select>
        <select className="field lg:!w-56 !py-2.5" value={params.get("status") || ""} onChange={(e) => set({ status: e.target.value })} data-testid="app-filter-status">
          <option value="">All statuses</option>{Object.entries(APP_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {isLoading ? <p className="text-slate-400">Loading…</p> : !items.length ? (
        <p className="border border-dashed border-white/20 p-12 text-center text-slate-300" data-testid="app-empty">No applications match. New applications from the Careers page appear here.</p>
      ) : (
        <div className="border hairline divide-y divide-white/10" data-testid="app-list">
          {items.map((a) => (
            <button key={a.id} onClick={() => set({ id: a.id })} className="w-full text-left p-4 md:p-5 bg-[var(--ink-2)] hover:bg-[var(--ink-3)] transition-colors" data-testid={`app-row-${a.id}`}>
              <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs text-gold">{a.ref}</span><Pill cls={statusCls(a.status)}>{APP_STATUS[a.status]}</Pill><Pill>{a.department}</Pill>{a.cover_letter && <Pill>Cover letter</Pill>}</div>
              <p className="mt-2 text-slate-100 font-medium truncate">{a.full_name} · {a.job_title}</p>
              <p className="text-sm text-slate-300 truncate">{a.email} · {[a.city, a.state, a.country].filter(Boolean).join(", ")}</p>
              <p className="text-xs text-slate-400 mt-1">Received {formatDateTime(a.created_at)}</p>
            </button>
          ))}
        </div>
      )}
      <Sheet open={!!openId} onOpenChange={(v) => !v && set({ id: "" })}>
        <SheetContent side="right" className="w-full sm:max-w-xl bg-[var(--ink)] border-white/10 overflow-y-auto" data-testid="app-sheet">
          <SheetHeader className="sr-only"><SheetTitle>Application details</SheetTitle><SheetDescription>Applicant details, documents, status and notes</SheetDescription></SheetHeader>
          {detail ? <Detail key={`${detail.id}-${detail.status}`} a={detail} onChange={refresh} onDeleted={() => { set({ id: "" }); refresh(); }} /> : <p className="text-slate-400 pt-10">Loading…</p>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
